// The chatscope base styles and the shared chat overrides used to reach the
// bundle only via the PubNub inbox (routes → InappChat → Pubnub/index.tsx).
// That route is gone, so this surface must import them itself.
import '@chatscope/chat-ui-kit-styles/dist/default/styles.min.css';
import './comms-chat.css';

import {
  ChatContainer,
  ConversationHeader,
  MessageInput,
  MessageList,
  MessageSeparator,
} from '@chatscope/chat-ui-kit-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Button as MuiButton, Stack as MuiStack } from '@mui/material';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useModal } from 'react-modal-hook';

import Avatar from '@/components/Avatar';
import LoadingState from '@/components/LoadingState';
import { Heading, Small } from '@/components/Typography';
import useAuth from '@/hooks/useAuth';
import { getDateandLabel, getTimeString } from '@/util/date';

import { useCloseConversation } from '../api/useCloseConversation';
import { useConversation } from '../api/useConversation';
import { useConversationMessages } from '../api/useConversationMessages';
import { useSendMessage } from '../api/useSendMessage';
import { useSendPhotoMessage } from '../api/useSendPhotoMessage';
import { useWorkersByIds, type WorkerChip } from '../api/useWorkersByIds';
import CloseChatModal from '../components/CloseChatModal';
import { MessageBubble } from '../components/MessageBubble';
import { SystemMessageCard } from '../components/SystemMessageCard';
import { resolveSenderLabel } from '../lib/senderLabel';
import { isLikelyHeic } from '../lib/heic';
import type { Message } from '../types';

interface CommsChatDetailProps {
  conversationId: string;
  onBack?: () => void;
  phoneOnly?: boolean;
}

export function CommsChatDetail({
  conversationId,
  onBack,
  phoneOnly = false,
}: CommsChatDetailProps) {
  const { currentAdmin } = useAuth();
  const { conversation, patchConversation } = useConversation(conversationId);
  const { messages, loading, loadingMore, hasMore, loadMore } =
    useConversationMessages(conversationId);
  const { send, sending } = useSendMessage();
  const { sendPhoto, sending: sendingPhoto } = useSendPhotoMessage();
  const { close, reopen, closing, reopening } = useCloseConversation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingError, setPendingError] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingPreviewUrl, setPendingPreviewUrl] = useState<string | null>(
    null,
  );

  const adminUserId = currentAdmin?.user?.id ?? '';
  const isClosed = !!conversation?.closed_at;
  const closedDateText = conversation?.closed_at
    ? new Date(conversation.closed_at).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '';

  const workerIds = useMemo(
    () => (conversation?.worker_id != null ? [conversation.worker_id] : []),
    [conversation?.worker_id],
  );
  const { workers } = useWorkersByIds(workerIds);
  const worker =
    conversation?.worker_id != null
      ? workers[conversation.worker_id]
      : undefined;
  const title = headerTitle(worker, conversation?.title ?? null);
  const workerUserId = worker?.user?.id ?? null;
  const workerName = useMemo(() => {
    if (!worker?.user) return null;
    const composed = `${worker.user.first_name ?? ''} ${
      worker.user.last_name ?? ''
    }`.trim();
    return composed || worker.user.email || null;
  }, [worker]);

  const onSend = useCallback(
    async (_message: string, _textContent: string, innerText: string) => {
      const text = innerText?.trim();
      if (!text) return;
      try {
        setPendingError(null);
        await send({ conversationId, content: text });
      } catch (err) {
        setPendingError(
          (err as Error)?.message ?? 'Failed to send. Try again.',
        );
      }
    },
    [conversationId, send],
  );

  const onAttachClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const onFileSelected = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      if (isLikelyHeic(file)) {
        setPendingError(
          "HEIC photos can't be displayed in the browser. Please convert the photo to JPEG or PNG and try again.",
        );
        return;
      }
      setPendingError(null);
      setPendingFile(file);
      setPendingPreviewUrl(URL.createObjectURL(file));
    },
    [],
  );

  const clearPending = useCallback(() => {
    setPendingFile(null);
    setPendingPreviewUrl((url) => {
      if (url) URL.revokeObjectURL(url);
      return null;
    });
  }, []);

  const onSendPending = useCallback(async () => {
    if (!pendingFile) return;
    try {
      await sendPhoto({ conversationId, file: pendingFile });
      clearPending();
    } catch (err) {
      setPendingError((err as Error)?.message ?? 'Failed to upload photo.');
    }
  }, [pendingFile, sendPhoto, conversationId, clearPending]);

  const workerNameForModal = useMemo(() => {
    if (!worker?.user) return conversation?.title ?? null;
    const first = worker.user.first_name ?? '';
    const last = worker.user.last_name ?? '';
    return `${first} ${last}`.trim() || worker.user.email || null;
  }, [worker, conversation?.title]);

  const [showCloseModal, hideCloseModal] = useModal(
    () => (
      <CloseChatModal
        workerName={workerNameForModal}
        hideModal={hideCloseModal}
        loading={closing}
        onConfirm={async (reason) => {
          if (!conversationId) return;
          try {
            const state = await close(conversationId, reason || undefined);
            patchConversation({
              closed_at: state.closed_at,
              closed_by_user_id: state.closed_by_user_id,
              closed_by_name: state.closed_by_name,
              closed_reason: state.closed_reason,
            });
            hideCloseModal();
          } catch (err) {
            setPendingError(
              (err as Error)?.message ?? 'Failed to close chat. Try again.',
            );
          }
        }}
      />
    ),
    [conversationId, workerNameForModal, closing, close, patchConversation],
  );

  const handleReopen = useCallback(async () => {
    if (!conversationId) return;
    try {
      setPendingError(null);
      const state = await reopen(conversationId);
      patchConversation({
        closed_at: state.closed_at,
        closed_by_user_id: state.closed_by_user_id,
        closed_by_name: state.closed_by_name,
        closed_reason: state.closed_reason,
      });
    } catch (err) {
      setPendingError(
        (err as Error)?.message ?? 'Failed to reopen chat. Try again.',
      );
    }
  }, [conversationId, reopen, patchConversation]);

  const orderedMessages = useMemo(
    () => [...messages].slice().reverse(),
    [messages],
  );

  return (
    <div
      id="comms-chat-detail"
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        background: '#fff',
      }}
    >
      {/* GRAVY-10213 layout rules, scoped to this chat surface:
          - #comms-chat-input padding makes room for our custom attach
            button (absolute, bottom-left) so the textarea isn't under it.
          - The conversation-header overrides keep the Close/Reopen action
            pinned and visible: our custom userName (avatar + Heading)
            doesn't truncate like chat-ui-kit's built-in name, so the
            default flex-grow:2 on __content let a long name shove the
            actions off-screen. Clamp the content and never shrink the
            actions. */}
      <style>{`
        #comms-chat-input .cs-message-input__content-editor-wrapper {
          padding-left: 36px;
        }
        #comms-chat-detail .cs-conversation-header__content {
          flex-grow: 1;
          min-width: 0;
          overflow: hidden;
        }
        #comms-chat-detail .cs-conversation-header__actions {
          flex-shrink: 0;
        }
        #comms-chat-detail #comms-chat-title {
          display: block;
          max-width: 100%;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      `}</style>
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <ChatContainer>
          <ConversationHeader
            id="comms-chat-header"
            style={{ backgroundColor: '#fff' }}
          >
            {phoneOnly && onBack ? (
              <ConversationHeader.Back id="btn-back" onClick={onBack} />
            ) : null}
            <ConversationHeader.Content
              id="comms-chat-header-content"
              userName={
                <MuiStack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{ minWidth: 0 }}
                >
                  <Avatar
                    firstName={title}
                    src={worker?.avatar_url ?? undefined}
                    size={phoneOnly ? 'sm' : 'm'}
                  />
                  <MuiStack
                    direction="column"
                    spacing={0.25}
                    sx={{ minWidth: 0, overflow: 'hidden' }}
                  >
                    <Heading id="comms-chat-title" title={title}>
                      {title}
                    </Heading>
                    {isClosed && (
                      <Small color="lighter" id="chat-closed-subtitle">
                        Closed {closedDateText}
                        {conversation?.closed_by_name
                          ? ` by ${conversation.closed_by_name}`
                          : ''}
                      </Small>
                    )}
                  </MuiStack>
                </MuiStack>
              }
            />
            <ConversationHeader.Actions>
              {isClosed ? (
                <MuiButton
                  id="btn-reopen-chat"
                  size="small"
                  variant="text"
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleReopen();
                  }}
                  disabled={reopening}
                >
                  {reopening ? 'Reopening…' : 'Reopen'}
                </MuiButton>
              ) : (
                <MuiButton
                  id="btn-close-chat"
                  size="small"
                  variant="text"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    showCloseModal();
                  }}
                >
                  Close chat
                </MuiButton>
              )}
            </ConversationHeader.Actions>
          </ConversationHeader>
          <MessageList
            className="custom-bg-chat"
            loadingMore={loadingMore}
            onYReachStart={() => {
              if (hasMore && !loading && !loadingMore) void loadMore();
            }}
          >
            {loading && messages.length === 0 ? (
              <LoadingState />
            ) : (
              orderedMessages.map((msg, idx) =>
                renderMessageNode({
                  msg,
                  prevMsg: idx > 0 ? orderedMessages[idx - 1] : null,
                  currentUserId: adminUserId,
                  workerUserId,
                  workerName,
                }),
              )
            )}
          </MessageList>
          {isClosed ? (
            <MessageInput
              attachButton={false}
              className="custom-bg-chat-disabled custom-chat-input"
              disabled={true}
              id="comms-chat-input-closed"
              placeholder={`Chat closed${
                closedDateText ? ` ${closedDateText}` : ''
              }. Reopen to send messages.`}
              sendButton={false}
            />
          ) : (
            <MessageInput
              attachButton={false}
              className="custom-bg-chat custom-chat-input"
              id="comms-chat-input"
              placeholder={
                pendingFile
                  ? 'Send or cancel the photo first'
                  : 'Type a message'
              }
              disabled={sending || sendingPhoto || !!pendingFile}
              onSend={onSend}
            />
          )}
        </ChatContainer>
        {!isClosed && (
          <button
            type="button"
            onClick={onAttachClick}
            disabled={sending || sendingPhoto || !!pendingFile}
            aria-label={sendingPhoto ? 'Uploading photo' : 'Attach photo'}
            title={sendingPhoto ? 'Uploading…' : 'Attach photo'}
            style={attachButtonStyle(sending || sendingPhoto || !!pendingFile)}
          >
            <FontAwesomeIcon
              icon={sendingPhoto ? faSpinner : faImage}
              spin={sendingPhoto}
              style={{ fontSize: 18 }}
            />
          </button>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={onFileSelected}
      />
      {pendingFile && pendingPreviewUrl ? (
        <div
          style={{
            flexShrink: 0,
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            background: '#f9fafb',
            padding: '10px 12px',
            margin: '6px 8px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <img
            src={pendingPreviewUrl}
            alt="Preview"
            style={{
              width: 64,
              height: 64,
              objectFit: 'cover',
              borderRadius: 6,
              flexShrink: 0,
            }}
          />
          <MuiStack direction="row" spacing={1} alignItems="center">
            <MuiButton
              size="small"
              variant="contained"
              disabled={sendingPhoto}
              onClick={() => void onSendPending()}
              startIcon={
                sendingPhoto ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : undefined
              }
            >
              {sendingPhoto ? 'Sending…' : 'Send'}
            </MuiButton>
            <MuiButton
              size="small"
              variant="text"
              color="inherit"
              disabled={sendingPhoto}
              onClick={clearPending}
            >
              Cancel
            </MuiButton>
          </MuiStack>
        </div>
      ) : null}
      {pendingError ? (
        <div
          role="alert"
          style={{
            flexShrink: 0,
            color: '#b91c1c',
            background: '#fee2e2',
            padding: '6px 12px',
            fontSize: 13,
          }}
        >
          {pendingError}
        </div>
      ) : null}
    </div>
  );
}

// Anchored over MessageInput's left edge; `bottom: 7` puts the 32×32
// button's center at ~23px, matching chat-ui-kit's built-in send icon.
function attachButtonStyle(disabled: boolean): React.CSSProperties {
  return {
    position: 'absolute',
    bottom: 7,
    left: 12,
    width: 32,
    height: 32,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    borderRadius: 6,
    background: 'transparent',
    color: disabled ? '#9ca3af' : '#6ea9d7',
    cursor: disabled ? 'not-allowed' : 'pointer',
    padding: 0,
    zIndex: 2,
  };
}

function headerTitle(
  worker: WorkerChip | undefined,
  fallbackTitle: string | null,
): string {
  if (worker?.user) {
    const first = worker.user.first_name ?? '';
    const last = worker.user.last_name ?? '';
    const composed = `${first} ${last}`.trim();
    if (composed.length > 0) return composed;
    return worker.user.email ?? fallbackTitle ?? 'Worker';
  }
  return fallbackTitle ?? 'Chat';
}

function renderMessageNode({
  msg,
  prevMsg,
  currentUserId,
  workerUserId,
  workerName,
}: {
  msg: Message;
  prevMsg: Message | null;
  currentUserId: string | number;
  workerUserId: number | null;
  workerName: string | null;
}) {
  const date = new Date(msg.created_at);
  const label = getDateandLabel(date);
  const time = getTimeString(date);
  const prevLabel = prevMsg
    ? getDateandLabel(new Date(prevMsg.created_at))
    : null;
  const showSeparator = label !== prevLabel;

  const senderLabel = resolveSenderLabel({
    message: msg,
    workerUserId,
    workerName,
  });

  const node =
    msg.message_type === 'system' ? (
      <SystemMessageCard message={msg} />
    ) : (
      <MessageBubble
        message={msg}
        currentUserId={currentUserId}
        senderLabel={senderLabel}
        time={time}
      />
    );

  if (showSeparator) {
    return (
      <span key={msg.id}>
        <MessageSeparator className="custom-chat-line">
          {label}
        </MessageSeparator>
        {node}
      </span>
    );
  }
  return <span key={msg.id}>{node}</span>;
}
